import type * as Apollo from "@apollo/client";
import { useCallback } from "react";

// Stable no-op for callers in flows that have no wizard cache to refetch
// (e.g. template-edit, task-edit, BoM, purchase-order item details). The
// `refetchWizardQuestions` prop is required on wizard-affecting boxes to
// preserve the no-silent-forgetting compile-time guarantee; callers in
// non-wizard contexts pass this constant explicitly to acknowledge that
// the box's mutations have no wizard cache to invalidate in their flow.
export const noopRefetchWizardQuestions = (): Promise<void> =>
  Promise.resolve();

type WithWizardRefetchOptions<
  TData,
  TVariables extends Apollo.OperationVariables,
> = Apollo.MutationHookOptions<TData, TVariables> & {
  refetchWizardQuestions: () => Promise<unknown>;
};

export function withWizardRefetch<
  TData,
  TVariables extends Apollo.OperationVariables,
>(
  useUnderlyingMutation: (
    baseOptions?: Apollo.MutationHookOptions<TData, TVariables>
  ) => Apollo.MutationTuple<TData, TVariables>
): (
  options: WithWizardRefetchOptions<TData, TVariables>
) => Apollo.MutationTuple<TData, TVariables> {
  return function useMutationWithWizardRefetch(options) {
    const { refetchWizardQuestions, ...baseOptions } = options;

    const [mutate, result] = useUnderlyingMutation(baseOptions);

    const wrappedMutate = useCallback<typeof mutate>(
      async mutationOptions => {
        const fetchResult = await mutate(mutationOptions);
        // The refetch is a side-effect, not a precondition of the mutation
        // succeeding. A failed refetch must not propagate to the caller —
        // the wizard cache stays stale until the next refresh, but the
        // mutation result is already correct.
        void refetchWizardQuestions().catch(() => {});
        return fetchResult;
      },
      [mutate, refetchWizardQuestions]
    );

    return [wrappedMutate, result];
  };
}
